import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Euro as EuroIcon,
  Payment as PaymentIcon,
  PhotoCamera as PhotoCameraIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Receipt as ReceiptIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import {
  FormCard,
  BackButton,
  SaveButton,
  SecondaryButton,
} from './UI';
import { Donor, CreateDonorRequest, CreateDonationRequest, PaymentMethod } from '@/types';

interface DonationSummaryStepProps {
  data: {
    selectedDonor?: Donor;
    newDonor?: CreateDonorRequest;
    donation: Partial<CreateDonationRequest>;
    paymentPhoto?: File;
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
    };
  };
  onSubmit: () => void;
  onBack: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isOnline: boolean;
  showNavigationButtons?: boolean;
  isDataConfirmed?: boolean;
  onDataConfirmedChange?: (confirmed: boolean) => void;
}

const getPaymentMethodLabel = (method: PaymentMethod) => {
  const labels = {
    CASH: 'Contanti',
    CARD: 'Carta di Credito/Debito',
    BANK_TRANSFER: 'Bonifico Bancario',
    CHECK: 'Assegno',
    OTHER: 'Altro',
  };
  return labels[method] || method;
};

export const DonationSummaryStep: React.FC<DonationSummaryStepProps> = ({
  data,
  onSubmit,
  onBack,
  onCancel,
  isSubmitting,
  isOnline,
  showNavigationButtons = true,
  isDataConfirmed = false,
  onDataConfirmedChange,
}) => {
  const donor = data.selectedDonor || data.newDonor;
  const isNewDonor = !data.selectedDonor;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Riepilogo Donazione
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Verifica tutti i dati prima di confermare la creazione della donazione.
      </Typography>

      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 3, width: '100%' }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            <strong>Modalità Offline:</strong> La donazione verrà salvata localmente 
            e sincronizzata quando tornerai online.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Informazioni Donatore */}
        <Grid item xs={12} lg={6}>
          <FormCard
            title="Donatore"
            subtitle={isNewDonor ? 'Nuovo donatore' : 'Donatore esistente'}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 2,
              flexWrap: 'wrap'
            }}>
              <Avatar sx={{ bgcolor: 'primary.main', flexShrink: 0 }}>
                <PersonIcon />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                  {donor?.firstName} {donor?.lastName}
                </Typography>
                <Chip
                  label={isNewDonor ? 'Nuovo' : 'Esistente'}
                  color={isNewDonor ? 'primary' : 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

            <List dense>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <EmailIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Email"
                  secondary={donor?.email}
                  sx={{ 
                    '& .MuiListItemText-secondary': {
                      wordBreak: 'break-word'
                    }
                  }}
                />
              </ListItem>
              {donor?.phone && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhoneIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Telefono"
                    secondary={donor.phone}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
              {donor?.address && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Indirizzo"
                    secondary={`${donor.address}, ${donor.city} ${donor.postalCode}`}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
              {donor?.company && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <BusinessIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Azienda"
                    secondary={donor.company}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
              {donor?.fiscalCode && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <BadgeIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Codice Fiscale"
                    secondary={donor.fiscalCode}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
              {donor?.vatNumber && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ReceiptIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Partita IVA"
                    secondary={donor.vatNumber}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
              {donor?.sdiCode && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CodeIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Codice SDI"
                    secondary={donor.sdiCode}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
              {donor?.pec && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EmailIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="PEC"
                    secondary={donor.pec}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
            </List>

            {isNewDonor && (
              <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  Questo donatore verrà creato nel sistema insieme alla donazione.
                </Typography>
              </Alert>
            )}
          </FormCard>
        </Grid>

        {/* Informazioni Donazione */}
        <Grid item xs={12} lg={6}>
          <FormCard
            title="Donazione"
            subtitle="Dettagli della donazione"
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 2,
              flexWrap: 'wrap'
            }}>
              <Avatar sx={{ bgcolor: 'success.main', flexShrink: 0 }}>
                <EuroIcon />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography 
                  variant="h4" 
                  color="success.main"
                  sx={{ 
                    fontSize: { xs: '2rem', sm: '2.5rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  €{Number(data.donation.amount || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Importo donazione
                </Typography>
              </Box>
            </Box>

            <List dense>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PaymentIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Metodo di Pagamento"
                  secondary={getPaymentMethodLabel(data.donation.paymentMethod as PaymentMethod)}
                  sx={{ 
                    '& .MuiListItemText-secondary': {
                      wordBreak: 'break-word'
                    }
                  }}
                />
              </ListItem>
              {data.paymentPhoto && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhotoCameraIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Foto Pagamento"
                    secondary={`${data.paymentPhoto.name} (${(data.paymentPhoto.size / 1024).toFixed(1)} KB)`}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
              {data.donation.notes && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Note"
                    secondary={data.donation.notes}
                    sx={{ 
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              )}
            </List>
          </FormCard>
        </Grid>

        {/* Informazioni Posizione */}
        {data.location && (
          <Grid item xs={12}>
            <FormCard
              title="Posizione"
              subtitle="Luogo della donazione"
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 2,
                flexWrap: 'wrap'
              }}>
                <LocationIcon color="action" sx={{ mt: 0.5, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    variant="body1"
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {data.location.address || 'Posizione GPS'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      wordBreak: 'break-word',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    Lat: {data.location.latitude.toFixed(6)}, 
                    Lng: {data.location.longitude.toFixed(6)}
                  </Typography>
                </Box>
              </Box>
            </FormCard>
          </Grid>
        )}

        {/* Checkbox Conferma Dati */}
        <Grid item xs={12}>
          <Card sx={{ 
            bgcolor: 'primary.50', 
            border: '2px solid', 
            borderColor: 'primary.main',
            overflow: 'hidden'
          }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDataConfirmed}
                    onChange={(e) => onDataConfirmedChange?.(e.target.checked)}
                    color="primary"
                    sx={{ 
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography 
                    variant="h6"
                    sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      wordBreak: 'break-word'
                    }}
                  >
                    ✓ Dati confermati
                  </Typography>
                }
                sx={{ 
                  width: '100%',
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    marginLeft: 1,
                  }
                }}
              />
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  mt: 1,
                  ml: 5,
                  wordBreak: 'break-word'
                }}
              >
                Confermo che tutti i dati inseriti sono corretti e posso procedere con la creazione della donazione.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Riepilogo Finale */}
        <Grid item xs={12}>
          <Card sx={{ 
            bgcolor: 'grey.50', 
            border: '1px solid', 
            borderColor: 'grey.200',
            overflow: 'hidden'
          }}>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 2,
                flexWrap: 'wrap'
              }}>
                <CheckCircleIcon color="success" sx={{ flexShrink: 0 }} />
                <Typography 
                  variant="h6"
                  sx={{ wordBreak: 'break-word' }}
                >
                  Riepilogo Finale
                </Typography>
              </Box>
              
              <Typography 
                variant="body1" 
                paragraph
                sx={{ wordBreak: 'break-word' }}
              >
                Stai per creare una donazione di <strong>€{Number(data.donation.amount || 0).toFixed(2)}</strong> 
                {' '}per <strong>{donor?.firstName} {donor?.lastName}</strong> 
                {isNewDonor ? ' (nuovo donatore)' : ''}.
              </Typography>

              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ wordBreak: 'break-word' }}
              >
                {isOnline 
                  ? 'La donazione verrà inviata immediatamente al server.'
                  : 'La donazione verrà salvata localmente e sincronizzata quando tornerai online.'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {showNavigationButtons && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 4,
          flexWrap: 'wrap',
          gap: 2,
          width: '100%'
        }}>
          <BackButton onClick={onBack} />
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <SecondaryButton 
              onClick={onCancel}
              sx={{ 
                minWidth: { xs: 'auto', sm: 'auto' },
                flex: { xs: '1 1 auto', sm: '0 0 auto' }
              }}
            >
              Annulla
            </SecondaryButton>
            <SaveButton
              onClick={onSubmit}
              loading={isSubmitting}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
